import { Link } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { resolveBookingUrl } from "@/lib/publicUrls";
import {
  onlineBookingTabHref,
  onlineBookingTabHrefWithSection,
  type OnlineBookingTab,
} from "@/pages/stammdaten/onlineBookingSections";

type SetupStep = {
  title: string;
  detail: string;
  tab: OnlineBookingTab;
  section?: string;
};

const GUEST_FLOW_STEPS = [
  {
    title: "Zeitraum wählen",
    detail: "Anreise- und Abreisedatum; optional Strom, Kinder und Hunde.",
  },
  {
    title: "Platzkategorie wählen",
    detail: "Verfügbare Kategorien mit Preis und optionalen Marketing-Texten.",
  },
  {
    title: "Anreiseart & Personen",
    detail: "Wohnmobil, Zelt o. Ä. – abhängig von Ihren freigegebenen Anreisearten.",
  },
  {
    title: "Kontakt & Mitreisende",
    detail: "Hauptreisende/r und ggf. weitere Personen mit den von Ihnen festgelegten Feldern.",
  },
  {
    title: "Zusatzartikel & Absenden",
    detail: "Optionale Extras buchen, Preis prüfen und Anfrage oder Buchung absenden.",
  },
];

const SETUP_STEPS: SetupStep[] = [
  {
    title: "Website aktivieren und Buchungsmodus wählen",
    detail: "Legen Sie fest, ob Gäste anfragen oder sofort buchen dürfen.",
    tab: "betrieb",
  },
  {
    title: "Saisons und Mindestaufenthalt festlegen",
    detail: "Steuern Sie, wann online gebucht werden darf und wie lang Aufenthalte mindestens sein müssen.",
    tab: "regeln",
    section: "saisons",
  },
  {
    title: "Buchungsschritte und Gastfelder konfigurieren",
    detail: "Bestimmen Sie, welche Schritte und Angaben Gäste ausfüllen müssen.",
    tab: "strecke",
    section: "schritte",
  },
  {
    title: "Anreisearten und Zusatzartikel pflegen",
    detail: "Bieten Sie passende Anreisearten an und ergänzen Sie optionale Extras.",
    tab: "angebot",
    section: "anreisearten",
  },
  {
    title: "Kategorie-Darstellung und Upselling einrichten",
    detail: "Präsentieren Sie Ihr Angebot verkaufsstark auf der Buchungswebsite.",
    tab: "angebot",
    section: "darstellung",
  },
];

const CONFIG_AREAS = [
  {
    title: "Betrieb",
    detail: "Website aktiv, Buchungsmodus, E-Mails",
    tab: "betrieb" as const,
  },
  {
    title: "Verfügbarkeit",
    detail: "Saisons, Mindestaufenthalt, Same-Day-Cutoff",
    tab: "regeln" as const,
    section: "saisons",
  },
  {
    title: "Buchungsstrecke",
    detail: "Schritte, Strom, Kinder, Hunde, Mitreisende",
    tab: "strecke" as const,
    section: "schritte",
  },
  {
    title: "Gastdaten",
    detail: "Pflicht- und Optionalfelder für Hauptreisende und Mitreisende",
    tab: "strecke" as const,
    section: "gastdaten",
  },
  {
    title: "Angebot",
    detail: "Anreisearten, Zusatzartikel, Kategorie-Darstellung",
    tab: "angebot" as const,
    section: "anreisearten",
  },
];

export function OnlineBookingIntroPanel() {
  const bookingUrl = resolveBookingUrl();

  return (
    <div className="space-y-6">
      <Card
        title="Was ist die Online-Buchung?"
        description="Die öffentliche Buchungswebsite für Gäste – unabhängig von der Rezeptions-Software."
        bodyClassName="space-y-4"
      >
        <p className="text-sm text-label-secondary">
          Gäste buchen oder stellen eine Anfrage über Ihre eigene Buchungswebsite. Die Rezeption
          sieht eingehende Buchungen im Dashboard und kann Anfragen bestätigen, Plätze zuweisen und
          Rechnungen erstellen – wie bei telefonischen oder schriftlichen Reservierungen.
        </p>
        <div className="rounded-lg border border-separator bg-fill-quaternary px-4 py-3 text-sm">
          <p className="font-medium text-label">Öffentliche Buchungswebsite</p>
          <a
            href={bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 block break-all text-accent underline"
          >
            {bookingUrl}
          </a>
        </div>
      </Card>

      <Card
        title="Konfigurationsbereiche"
        description="Die Einstellungen sind in fünf Bereiche gegliedert"
        bodyClassName="space-y-3"
      >
        <ul className="grid gap-2 sm:grid-cols-2">
          {CONFIG_AREAS.map((area) => (
            <li key={area.title}>
              <Link
                to={
                  area.section
                    ? onlineBookingTabHrefWithSection(area.tab, area.section)
                    : onlineBookingTabHref(area.tab)
                }
                className="block rounded-lg border border-separator px-4 py-3 transition-colors hover:border-accent/40 hover:bg-fill-quaternary"
              >
                <p className="text-sm font-medium text-label">{area.title}</p>
                <p className="mt-1 text-sm text-label-secondary">{area.detail}</p>
              </Link>
            </li>
          ))}
        </ul>
      </Card>

      <Card
        title="So erleben Gäste die Buchung"
        description="Typischer Ablauf von der Datumsauswahl bis zur Bestätigung"
        bodyClassName="space-y-3"
      >
        <ol className="space-y-3">
          {GUEST_FLOW_STEPS.map((step, index) => (
            <li key={step.title} className="flex gap-3 text-sm">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-semibold text-accent">
                {index + 1}
              </span>
              <div>
                <p className="font-medium text-label">{step.title}</p>
                <p className="text-label-secondary">{step.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </Card>

      <Card
        title="Einrichtung – empfohlene Reihenfolge"
        description="Fünf Schritte von der Aktivierung bis zur Darstellung"
        bodyClassName="space-y-3"
      >
        <ol className="space-y-3">
          {SETUP_STEPS.map((step, index) => (
            <li key={step.title} className="rounded-lg border border-separator px-4 py-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-label">
                    {index + 1}. {step.title}
                  </p>
                  <p className="mt-1 text-sm text-label-secondary">{step.detail}</p>
                </div>
                <Link
                  to={
                    step.section
                      ? onlineBookingTabHrefWithSection(step.tab, step.section)
                      : onlineBookingTabHref(step.tab)
                  }
                  className="shrink-0 text-sm font-medium text-accent hover:underline"
                >
                  Bereich öffnen →
                </Link>
              </div>
            </li>
          ))}
        </ol>
      </Card>

      <Card
        title="Anfrage vs. sofortige Buchung"
        description="Der wichtigste Unterschied im Betrieb"
        bodyClassName="space-y-3 text-sm text-label-secondary"
      >
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-lg border border-separator p-4">
            <p className="font-medium text-label">Anfragen (empfohlen zum Start)</p>
            <p className="mt-2">
              Gäste senden eine Buchungsanfrage. Die Rezeption prüft Verfügbarkeit und bestätigt
              manuell. Ideal, wenn Sie neue Einstellungen testen oder Kapazität flexibel steuern
              möchten.
            </p>
          </div>
          <div className="rounded-lg border border-separator p-4">
            <p className="font-medium text-label">Sofort buchen (Reservierung)</p>
            <p className="mt-2">
              Verfügbare Plätze werden sofort reserviert. Die Verfügbarkeitsprüfung ist dabei
              immer aktiv. Geeignet, wenn Prozesse stabil laufen und Sie weniger manuelle
              Bestätigung wünschen.
            </p>
          </div>
        </div>
        <p>
          Beide Modi konfigurieren Sie unter{" "}
          <Link to={onlineBookingTabHref("betrieb")} className="text-accent hover:underline">
            Betrieb
          </Link>
          .
        </p>
      </Card>
    </div>
  );
}
